import axios from 'axios';
import { env } from 'env';

import type {
  IRealtimeEntitySnapshot,
  RealtimeEntityPropertyValue,
} from '@interfaces/realtimeEntities';

/**
 * One saved spawned-entity snapshot as the backend stores and returns it.
 * Mirrors spatial-manager's `ISpawnedEntity`.
 */
export interface ISpawnedEntityApi {
  brandingId: string;
  entityId: string;
  type: string;
  name: string;
  position?: number[];
  rotation?: number[];
  scale?: number[];
  properties?: Record<string, RealtimeEntityPropertyValue>;
  spawnedBy: string;
  timestamp: number;
  updatedAt: number;
}

/** The snapshot body a save sends — the shared registry snapshot shape. */
export type ISaveSpawnedEntityData = IRealtimeEntitySnapshot;

/**
 * REST client for the saved snapshots of real-time spawned entities. The live
 * entities are engine state driven over the WebSocket; these calls persist and
 * list only what an author explicitly saved.
 *
 * A saved set belongs to a branding, like the spots and hotspots placed with
 * it; the room in the path is the notification target for the other admins and
 * the engine.
 */
export default class SpawnedEntitiesService {
  /**
   * Lists a branding's saved entities.
   *
   * @param roomId - Room the editor has open.
   * @param brandingId - Branding whose saved entities are read.
   * @returns The saved snapshots; empty when the branding has none.
   */
  static getBrandingSpawnedEntities(
    roomId: string,
    brandingId: string,
  ): Promise<ISpawnedEntityApi[]> {
    return axios
      .get<ISpawnedEntityApi[]>(
        `${env.REACT_APP_API_URL}api/v2/rooms/${roomId}/brandings/${brandingId}/spawned-entities`,
      )
      .then((res) => res.data);
  }

  /**
   * Saves one entity snapshot, replacing any prior save of the same id.
   *
   * @param roomId - Room whose live clients are told about the change.
   * @param brandingId - Branding the entity belongs to.
   * @param data - The snapshot to store.
   * @returns The stored record, with the author and timestamps the backend set.
   */
  static saveSpawnedEntity(
    roomId: string,
    brandingId: string,
    data: ISaveSpawnedEntityData,
  ): Promise<ISpawnedEntityApi> {
    return axios
      .post<ISpawnedEntityApi>(
        `${env.REACT_APP_API_URL}api/v2/rooms/${roomId}/brandings/${brandingId}/spawned-entities`,
        data,
      )
      .then((res) => res.data);
  }

  /**
   * Deletes one saved entity.
   *
   * @param roomId - Room whose live clients are told about the change.
   * @param brandingId - Branding the entity belongs to.
   * @param entityId - Wire id of the saved entity.
   * @returns Nothing; rejects with 404 when nothing is saved under the id.
   */
  static deleteSpawnedEntity(
    roomId: string,
    brandingId: string,
    entityId: string,
  ): Promise<void> {
    return axios
      .delete<void>(
        `${env.REACT_APP_API_URL}api/v2/rooms/${roomId}/brandings/${brandingId}/spawned-entities/${entityId}`,
      )
      .then((res) => res.data);
  }
}
